/* eslint-disable no-case-declarations */
import { AfterViewInit, Component, DestroyRef, Input, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MenuAction } from 'src/app/shared/enums/menu.action';
import { ContextMenuItem } from '../collection-list-widget/classes/context-menu.item';
import { ContextMenuItemService } from '../collection-list-widget/services/context-menu-item.service';
import { Store } from '@ngrx/store';
import { workspaceSidebarFeature } from '../../../../store/features/workspace-sidebar/workspace-sidebar-feature';
import {
  ContextMenuComponent as KendoContextMenuComponent,
  ContextMenuModule,
  ContextMenuSelectEvent,
} from '@progress/kendo-angular-menu';
import {
  ActionSheetComponent as KendoActionSheetComponent,
  ActionSheetModule,
} from '@progress/kendo-angular-navigation';
import { Subject, take, takeUntil, tap } from 'rxjs';
import { IconComponent } from '../../../../shared/components/ui/icon/icon.component';
import { CollectionListDataInstance } from '../../../../shared/interfaces/collection-list-data-instance';
import { GridService } from '../collection-list-widget/services/grid/grid.service';
import { ContextMenuService } from '../../../../shared/services/context-menu/context-menu.service';
import { trainingActions } from '../../../../store/features/training/training-actions';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { LinkingService } from 'src/app/core/services/linking/linking.service';
import {
  CollectionFormFileUploadApiService,
} from '../../../../api/bizzmine/collection-form-file-upload/collection-form-file-upload-api.service';
import {
  CollectionFormEditApiService,
} from '../../../../api/bizzmine/collection-form-edit/collection-form-edit-api.service';
import {
  UnlinkFromFolderParams,
} from 'src/app/shared/components/modals/unlink-from-folder-modal/interfaces/unlink-from-folder-params';
import { Dialog } from '@angular/cdk/dialog';
import { EditOnlineApiService } from '../../../../api/bizzmine/edit-online/edit-online-api.service';
import {
  FileUploadEditOnlineModalComponentMode,
} from '../../../../../models/ts/file-upload-edit-online-modal-component-mode';
import { FileUploadCheckinService } from '../../../../core/services/file-upload/file-upload-checkin.service';
import { refreshActions } from 'src/app/store/features/refresh/refresh-actions';
import { TrainingApiService } from 'src/app/api/bizzmine/training/training-api.service';
import { TrainingDto } from 'src/models/ts/training-dto.model';
import {
  TrainingSubscribeModalComponent,
  TrainingSubscribeModalComponentMode,
} from 'src/app/features/training/modal/training-subscribe-modal/training-subscribe-modal.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DocumentEventsService } from 'src/app/shared/services/document-events/document-events.service';
import { EditOnlineService } from '../../../../core/services/file-upload/edit-online.service';

/**
 * kendo contextmenu containing all actions for an instance.
 */
@Component({
  selector: 'bizz-context-menu',
  standalone: true,
  imports: [CommonModule, ContextMenuModule, IconComponent, ActionSheetModule],
  templateUrl: './context-menu.component.html',
  styleUrls: ['./context-menu.component.scss'],
})
@Sentry.TraceClass({ name: 'ContextMenuComponent' })
export class ContextMenuComponent implements AfterViewInit, OnDestroy {
  @ViewChild('menu', { static: false }) public menu!: KendoContextMenuComponent;
  @ViewChild('actionsheet', { static: true }) public actionsheet!: KendoActionSheetComponent;


  @Input() public useSeparators: boolean;
  public dataItem: CollectionListDataInstance | any;

  public menuItems: Partial<ContextMenuItem>[] = [];
  public isMobileView = false;
  public expanded = this.contextMenuService.getObservable();
  private workspaceIdSignal = this.store$.selectSignal(workspaceSidebarFeature.selectID);
  /**
   * Subject used to unsubscribe from all subscriptions when the component is destroyed.
   */
  private destroy: Subject<void> = new Subject<void>();

  private intersectionObserver: IntersectionObserver | undefined;

  public constructor(
    private gridService: GridService,
    private contextMenuItemService: ContextMenuItemService,
    private contextMenuService: ContextMenuService,
    private store$: Store,
    private router: Router,
    private linkingService: LinkingService,
    private fileUploadCheckinService: FileUploadCheckinService,
    private collectionFormFileUploadApiService: CollectionFormFileUploadApiService,
    private collectionFormEditApiService: CollectionFormEditApiService,
    private dialog: Dialog,
    private editOnlineApiServiceService: EditOnlineApiService,
    private editOnlineService: EditOnlineService,
    private trainingApiService: TrainingApiService,
    private documentEventsService: DocumentEventsService,
    private destroyRef: DestroyRef) {
    this.documentEventsService.isMobile.pipe(
      takeUntilDestroyed(this.destroyRef)).subscribe((isMobile: boolean) => {
        this.isMobileView = isMobile;
      },
    );
  }

  public ngAfterViewInit(): void {
    this.expanded.pipe(takeUntil(this.destroy)).subscribe({
      next: (expanded) => {
        if (expanded) this.open();
        else {
          if (this.isMobileView) {
            this.actionsheet.toggle(false);
          } else {
            this.menu.hide();
          }
        }
      },
    });

    this.intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) {
        this.close();
      }
    });
  }

  @Sentry.TraceMethod({ name: 'open' })
  public open(): void {
    this.dataItem = this.contextMenuService.dataItem;
    const permissions = this.contextMenuService.permissions;
    const anchor = this.contextMenuService.anchorElement;
    const useSeparators = this.contextMenuService.useSeparators;

    this.intersectionObserver?.disconnect();
    this.intersectionObserver?.observe(anchor?.nativeElement);

    if (!this.dataItem || !permissions || !anchor) {
      throw new Error('Missing data for context menu');
    }

    this.menuItems = this.contextMenuItemService.getMenuItems(permissions, this.dataItem, this.isMobileView, useSeparators);

    if (this.isMobileView) {
      this.actionsheet.toggle(true);
    } else this.menu.show(anchor);
  }

  @Sentry.TraceMethod({ name: 'close' })
  public close(): void {
    this.contextMenuService.close();
  }

  public onContextMenuSelectEvent($event: ContextMenuSelectEvent): void {
    if ($event.item.items?.length > 0)
      return; // Do not handle selection if the item has sub-items
    this.onItemSelected($event.item);
  }

  public onActionSheetItemClick(item: Partial<ContextMenuItem>): void {
    if (item.items && item.items?.length > 0) {
      item.expanded = !item.expanded;
      return; // Do not handle selection if the item has sub-items
    }
    this.onItemSelected(item);
    this.close();
  }

  public onItemSelected(item: Partial<ContextMenuItem>): void {
    if (!item || typeof item.action === 'undefined') return;
    this.handleMenuAction(item.action);
    if (this.isMobileView) {
      this.actionsheet.toggle(false);
    } else this.menu.hide();
  }

  @Sentry.TraceMethod({ name: 'ngOnDestroy' })
  public ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.unsubscribe();
    this.intersectionObserver?.disconnect();
  }

  private handleMenuAction(action: MenuAction): void {
    switch (action) {
      //TODO: RV, GL, BVB Add other menu actions
      case MenuAction.Read:
        this.gridService.readInstance(
          this.dataItem.CollectionsID,
          this.dataItem.ID,
          this.dataItem.VersionsID,
          this.dataItem.GuidChecksum,
          this.dataItem.DraftsID,
          this.dataItem.OriginalFoldersID,
          true,
          this.contextMenuService.permissions?.isTrashFilterActive,
        );
        break;
      case MenuAction.Edit:
        this.gridService.editInstance(
          this.dataItem.CollectionsID,
          this.dataItem.ID,
          this.dataItem.VersionsID,
          this.dataItem.GuidChecksum,
          this.dataItem.DraftsID,
          this.dataItem.OriginalFoldersID,
        );
        break;
      case MenuAction.Execute:
        this.gridService.openNextStep(
          undefined,
          this.dataItem.CollectionsID,
          this.dataItem.ID,
          this.dataItem.VersionsID,
          this.dataItem.TasksID,
          this.dataItem.DraftsID,
        );
        break;
      case MenuAction.RecoverInstance:
        this.gridService.restoreInstance(this.dataItem);
        break;
      case MenuAction.RestartWorkflow:
        this.gridService.restartWorkflow(this.dataItem);
        break;
      case MenuAction.Delete:
        this.gridService.deleteInstance(this.dataItem);
        break;
      case MenuAction.Monitor:
        this.gridService.openMonitorModal(this.dataItem.CollectionsID, this.dataItem.ID, this.dataItem.DraftsID !== 0 ? this.dataItem.DraftsID : this.dataItem.VersionsID);
        break;
      case MenuAction.History:
        this.gridService.openHistoryPage(
          this.dataItem.CollectionsID,
          this.dataItem.ID,
          this.dataItem.VersionsID,
          this.workspaceIdSignal(),
        );
        break;
      case MenuAction.Archive: {
        const permissions = this.contextMenuService.permissions;

        if (!permissions || !this.dataItem) {
          throw new Error('Missing data for context menu');
        }

        this.gridService.openArchiveModal(this.dataItem, permissions);
        break;
      }
      case MenuAction.TrackChanges:
        this.gridService.openTrackchangesPage(this.dataItem.CollectionsID, this.dataItem.ID, this.dataItem.VersionsID);
        break;
      case MenuAction.FlowStatus:
        this.gridService.openFlowStatusPage(
          this.dataItem.CollectionsID,
          this.dataItem.ID,
          this.dataItem.DraftsID != undefined && this.dataItem.DraftsID > 0 ? this.dataItem.DraftsID : this.dataItem.VersionsID,
          this.dataItem.MethodType ?? this.dataItem.DocumentFlow,
          this.workspaceIdSignal(),
          true,
        );
        break;
      case MenuAction.SubscribeToExam:
        this.store$.dispatch(
          trainingActions.fetchExam({
            collectionsId: <number>this.dataItem.CollectionsID,
            versionsId: <number>this.dataItem.VersionsID,
            config: {
              openExamDialog: true,
              redirectUrl: this.router.url,
            },
          }),
        );
        break;
      case MenuAction.ConsultRecord:
        this.gridService.readInstance(this.dataItem.CollectionsID, this.dataItem.ID, this.dataItem.VersionsID, this.dataItem.GuidChecksum, this.dataItem.DraftsID, this.dataItem.OriginalFoldersID);
        break;
      case MenuAction.DownloadFile:
        this.gridService.downloadFromInstance(this.dataItem);
        break;
      case MenuAction.CopyLinkToRecord:
        this.linkingService.copyLinkToRecord(this.dataItem.CollectionsID, this.dataItem.ID, this.dataItem.VersionsID, this.dataItem.GuidChecksum);
        break;
      case MenuAction.DocumentSettings:
        this.gridService.openDocumentSettings(this.dataItem.CollectionsID, this.dataItem.ID, this.dataItem.VersionsID);
        break;
      case MenuAction.Permissions:
        this.gridService.openPermissionsPage(
          this.dataItem.CollectionsID,
          this.dataItem.ID,
          this.dataItem.VersionsID,
          this.dataItem.OriginalFoldersID,
          this.dataItem.InheritType,
          null,
          null,
          false,
        );
        break;
      case MenuAction.UnlinkFromFolder:
        const widgetId = this.contextMenuService.widgetId;
        if (!widgetId) throw new Error('WidgetId is missing');
        const unlinkFromFolderParams: UnlinkFromFolderParams = {
          collectionId: this.dataItem.CollectionsID,
          instanceId: this.dataItem.ID,
          versionId: this.dataItem.VersionsID,
          folderId: this.dataItem.FoldersID,
          widgetId: widgetId,
          inheritType: this.dataItem.InheritType,
          targetType: this.dataItem.TargetType,
        };
        this.gridService.openUnlinkFromFolderModal(unlinkFromFolderParams);
        break;
      case MenuAction.CheckInFile:
        this.fileUploadCheckinService.openCheckInDialog(this.dataItem, true);
        break;
      case MenuAction.UndoCheckoutFile: {
        let versionId: number = this.dataItem.VersionsID;
        if (this.dataItem.DraftsID != null && this.dataItem.DraftsID > 0) {
          versionId = this.dataItem.DraftsID;
        }
        this.collectionFormEditApiService.undoCheckOut({
          versionId: versionId,
          instanceId: this.dataItem.ID,
          collectionId: this.dataItem.CollectionsID,
        }).pipe(take(1), tap(() => this.store$.dispatch(refreshActions.refreshData({ collectionId: this.dataItem.CollectionsID }))))
          .subscribe();
      }
        break;
      case MenuAction.CheckoutFile : {
        let versionId: number = this.dataItem.VersionsID;
        if (this.dataItem.DraftsID != null && this.dataItem.DraftsID > 0) {
          versionId = this.dataItem.DraftsID;
        }
        this.fileUploadCheckinService.checkOutDocument({
          checkOut$: this.collectionFormFileUploadApiService.checkOut({
            collectionId: this.dataItem.CollectionsID,
            instanceId: this.dataItem.ID,
            versionId: versionId,
          }).pipe(take(1), tap(() => this.store$.dispatch(refreshActions.refreshData({ collectionId: this.dataItem.CollectionsID })))),
          storeData: undefined,
        });
      }
        break;
      case MenuAction.PutFileWithoutCheckIn:
        this.fileUploadCheckinService.openCheckInDialog(this.dataItem, false);
        break;
      case MenuAction.DuplicateExam:
        this.gridService.duplicateInstance(this.dataItem);
        break;
      case MenuAction.Report:
        this.gridService.openReportModal(this.dataItem.CollectionsID, this.dataItem.ID, this.dataItem.VersionsID, this.dataItem.ReportSettings, this.dataItem.GuidChecksum);
        break;
      case MenuAction.ViewOnline: {
        this.editOnlineService.openOnline(this.dataItem, FileUploadEditOnlineModalComponentMode.View);
      }
        break;
      case MenuAction.EditOnline:
        this.editOnlineService.openOnline(this.dataItem, FileUploadEditOnlineModalComponentMode.Edit);
        break;
      case MenuAction.CreateMajorChanges:
        this.gridService.createMajorRevision(this.dataItem.CollectionsID, this.dataItem.ID, this.dataItem.VersionsID);
        break;
      case MenuAction.CreateMinorChanges:
        this.gridService.createMinorRevision(this.dataItem.CollectionsID, this.dataItem.ID, this.dataItem.VersionsID);
        break;
      case MenuAction.CopyDirectLink:
        this.gridService.copyDirectLink(this.dataItem);
        break;
      case MenuAction.SubscribeToTraining:
        this.onSubscribeToTraining();
        break;
      case MenuAction.DownloadQrCodeToRecord:
        this.gridService.downloadQRCodeToRecord(this.dataItem);
        break;
      case MenuAction.DownloadQRCodeDirectLink:
        this.gridService.downloadQRCodeDirectLink(this.dataItem);
        break;
      case MenuAction.MakeOfflineAvailable: {
        const listId = this.contextMenuService.listId;
        const widgetId = this.contextMenuService.widgetId;
        this.gridService.makeOfflineAvailable(this.dataItem.CollectionsID, this.dataItem.ID, this.dataItem.VersionsID, this.dataItem.GuidChecksum, this.dataItem.FoldersID, widgetId, listId);
      }
        break;
      case MenuAction.RemoveOfflineAvailable: {
        this.gridService.removeOfflineAvailable(this.dataItem.CollectionsID, this.dataItem.ID, this.dataItem.VersionsID, this.dataItem.FoldersID);
      }
        break;
      default:
        throw new Error('Unhandled context menu action');
    }
  }

  private onSubscribeToTraining(): void {
    this.trainingApiService.getTraining({
      collectionId: this.dataItem.CollectionsID!,
      versionId: this.dataItem.VersionsID!,
      instanceId: this.dataItem.ID!,
    }).subscribe(found => {
      this.dialog.open<TrainingDto | undefined>(TrainingSubscribeModalComponent, {
        data: {
          data: found,
          enableNoPreference: true,
          enableCreateNewSession: false,
          mode: TrainingSubscribeModalComponentMode.Subscribe,
        },
      })
        .closed
        .subscribe((result: TrainingDto | undefined) => {
          if (result !== undefined) {
            this.addToQueue(found, result?.ID);
          }
        });
    });
  }

  public addToQueue(source: TrainingDto, sessionId: number | null): void {
    this.trainingApiService.subscribeToTraining({
      trainingId: source.ID,
      sessionId: sessionId ?? 0,
      trainingAppId: source.TrainingAppID,
    }).subscribe(() => {
    });
  }
}
